import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Shield, ShieldCheck, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

function TwoFactorPage() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [setupData, setSetupData] = useState<{ secret: string; qrCode: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['2fa'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/auth/2fa`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch 2FA status');
      return response.json();
    },
  });

  const setupMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/v1/auth/2fa/setup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to start 2FA setup');
      return response.json();
    },
    onSuccess: (data) => {
      setSetupData({ secret: data.secret, qrCode: data.qrCode });
      setError('');
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await fetch(`${API_URL}/v1/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Verification failed');
      }
      return response.json();
    },
    onSuccess: (data) => {
      setRecoveryCodes(data.recoveryCodes);
      setSetupData(null);
      setVerifyCode('');
      queryClient.invalidateQueries({ queryKey: ['2fa'] });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const disableMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/v1/auth/2fa`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to disable 2FA');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['2fa'] });
      setRecoveryCodes(null);
    },
  });

  const is2FAEnabled = data?.enabled;

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const subNavItems = [
    { to: '/security', label: 'Password' },
    { to: '/security/tokens', label: 'API Tokens' },
    { to: '/security/sessions', label: 'Sessions' },
    { to: '/security/two-factor', label: 'Two-Factor' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Two-Factor Authentication</h1>
        <p className="text-muted-foreground">Add an extra layer of security to your account</p>
      </div>

      <SubNav items={subNavItems} />

      <div className="max-w-2xl">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg bg-destructive/15 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {recoveryCodes && (
          <Card className="mb-6 border-yellow-500/30 bg-yellow-500/10">
            <CardContent className="p-4">
              <div className="text-sm font-medium text-yellow-400">
                Save your recovery codes! You won't see them again.
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, i) => (
                  <code
                    key={i}
                    className="rounded bg-background p-2 text-center font-mono text-sm"
                  >
                    {code}
                  </code>
                ))}
              </div>
              <Button className="mt-4" onClick={() => setRecoveryCodes(null)}>
                I've saved my codes
              </Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && !is2FAEnabled && !setupData && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <div>
                  <CardTitle>Enable 2FA</CardTitle>
                  <CardDescription>
                    Protect your account with two-factor authentication
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-muted-foreground">
                Two-factor authentication adds an extra layer of security to your account by
                requiring a code from your authenticator app in addition to your password.
              </p>
              <Button onClick={() => setupMutation.mutate()} disabled={setupMutation.isPending}>
                {setupMutation.isPending ? 'Setting up...' : 'Enable 2FA'}
              </Button>
            </CardContent>
          </Card>
        )}

        {setupData && (
          <Card>
            <CardHeader>
              <CardTitle>Setup Two-Factor Authentication</CardTitle>
              <CardDescription>
                Scan the QR code or enter the secret key in your authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Secret Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-muted p-3 font-mono text-sm">
                    {setupData.secret}
                  </code>
                  <Button variant="outline" size="sm" onClick={copySecret}>
                    <Copy className="mr-2 h-4 w-4" />
                    {copiedSecret ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="verifyCode">Enter the 6-digit code from your app</Label>
                <Input
                  id="verifyCode"
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-40 font-mono text-lg tracking-widest"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => verifyMutation.mutate(verifyCode)}
                  disabled={verifyCode.length !== 6 || verifyMutation.isPending}
                >
                  Verify & Enable
                </Button>
                <Button variant="outline" onClick={() => setSetupData(null)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoading && is2FAEnabled && !setupData && (
          <Card className="border-green-500/30 bg-green-500/5">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/20">
                  <ShieldCheck className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-green-400">2FA Enabled</CardTitle>
                  <CardDescription>
                    Your account is protected with two-factor authentication
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-muted-foreground">
                Your account has an additional layer of security. You'll need to enter a code from
                your authenticator app when signing in.
              </p>
              <Button
                variant="outline"
                className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => {
                  if (
                    confirm(
                      'Are you sure you want to disable 2FA? This will make your account less secure.'
                    )
                  ) {
                    disableMutation.mutate();
                  }
                }}
              >
                Disable 2FA
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/security/two-factor')({
  component: TwoFactorPage,
});
